import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { VxCardComponent } from '@ui/card/card.component';
import { VxCardHeaderComponent } from '@ui/card/card-header.component';
import { VxCellDirective } from '@ui/data-table/cell.directive';
import { VxDataTableComponent } from '@ui/data-table/data-table.component';
import { VxHeaderDirective } from '@ui/data-table/header.directive';
import { VxRowActionsDirective } from '@ui/data-table/row-actions.directive';
import { VxColumn } from '@ui/data-table/data-table.types';
import { VxEmptyStateComponent } from '@ui/empty-state/empty-state.component';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxIconButtonComponent } from '@ui/icon-button/icon-button.component';
import { VxIconName } from '@ui/icons';
import { VxItemCellComponent } from '@ui/item-cell/item-cell.component';
import { VxListRowComponent } from '@ui/list-row/list-row.component';
import { VxMenuComponent } from '@ui/menu/menu.component';
import { VxMenuItemComponent } from '@ui/menu/menu-item.component';
import { VxMenuTriggerDirective } from '@ui/menu/menu-trigger.directive';
import { VxPanelComponent } from '@ui/panel/panel.component';
import { VxSkeletonComponent } from '@ui/skeleton/skeleton.component';
import { DateAgoPipe } from 'src/app/core/pipes/date-ago.pipe';
import { PageHeaderComponent } from 'src/app/layout/page-header/page-header.component';
import {
  IncidentSchemaPriority,
  IncidentSchemaState,
} from 'src/app/models/incident-schema';
import { Monitor } from 'src/app/models/monitor';
import { CheckRoleDirective } from 'src/app/services/permission/permission.directive';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { SortEntities } from 'src/app/store/common/helpers';
import { isBusy, loadedOnce } from 'src/app/store/common/loading';
import { LoadActiveIncidents } from 'src/app/store/incidents/incidents.actions';
import { IncidentsStateModule } from 'src/app/store/incidents/incidents.state.module';
import {
  LoadMonitorsAction,
  UpdateMonitorAction,
} from 'src/app/store/monitors/monitors.actions';
import { MonitorState } from 'src/app/store/monitors/monitors.state';
import { MonitorsStateModule } from 'src/app/store/monitors/monitors.state.module';
import { LoadServices } from 'src/app/store/services/services.actions';
import { ServicesState } from 'src/app/store/services/services.state';
import { ServicesStateModule } from 'src/app/store/services/services.state.module';
import { UserState } from 'src/app/store/user/user.state';
import { IncidentPriorityLabelComponent } from '../incidents/components/incident-priority-label/incident-priority-label.component';
import { IncidentStatusLabelComponent } from '../incidents/components/incident-status-label/incident-status-label.component';
import {
  MonitorDisplayStatus,
  monitorHasAvailabilityData,
  monitorLiveStatus,
  monitorTarget,
  refineDownForRegions,
} from '../monitors/components/monitor-global-status';
import { MonitorSliValueComponent } from '../monitors/components/monitor-sli-value/monitor-sli-value.component';
import { MonitorStatusLabelComponent } from '../monitors/components/monitor-status-label/monitor-status-label.component';
import { IncidentsState } from 'src/app/store/incidents/incidents.state';
import { VxDialogService } from '@ui/dialog/dialog.service';
import { openCreateIncidentDialog } from 'src/app/pages/internal/incidents/components/v2/create-incident-dialog/create-incident-dialog.component';
import { creatableIncidentSchemas } from 'src/app/pages/internal/incidents/components/v2/create-incident-button/create-incident-button.component';
import { CreateIncidentButtonComponent } from 'src/app/pages/internal/incidents/components/v2/create-incident-button/create-incident-button.component';
import { probeTypeIcon } from 'src/app/models/probe-types';

/** How much of each list the dashboard shows before deferring to its page. */
const ACTIVE_INCIDENT_ROWS = 3;
const MONITOR_ROWS = 5;

/** The window the Monitor card's uptime covers — the badge on its column. */
const UPTIME_WINDOW_DAYS = 7;
const UPTIME_RESOLUTION_SECONDS = 60 * 60 * 24;

/** Day parts for the greeting, by local hour. */
const AFTERNOON_FROM = 12;
const EVENING_FROM = 18;

/** Down monitors first: an outage should never sit below a healthy row. */
const STATUS_RANK: Record<MonitorDisplayStatus, number> = {
  DOWN: 0,
  DOWN_SINGLE_REGION: 0,
  DEGRADED: 1,
  UNKNOWN: 2,
  OK: 3,
  NO_DATA: 4,
  PAUSED: 5,
};

interface IncidentRow {
  id: number;
  title: string;
  summary?: string;
  state: IncidentSchemaState;
  /** All states of the incident's schema version — status label progress. */
  states: IncidentSchemaState[];
  priority?: IncidentSchemaPriority;
  createdAt: Date;
}

interface MonitorRow {
  id: string;
  name: string;
  target?: string;
  icon: VxIconName;
  status: MonitorDisplayStatus;
  paused: boolean;
  monitor: Monitor;
}

interface ServiceChip {
  id: number;
  name: string;
  /** Backend-computed health color; undefined until health is known. */
  color?: string;
}

/**
 * The dashboard: a greeting, then one card per thing worth knowing on
 * arrival — the active incidents, how the monitors are doing, and where each
 * service stands. Every card shows the first few rows and hands off to its
 * own page through the header menu; nothing here is a place to work, only to
 * notice.
 */
@Component({
  selector: 'app-page-home',
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CreateIncidentButtonComponent,
    RouterLink,
    TranslocoModule,
    DateAgoPipe,
    PageHeaderComponent,
    CheckRoleDirective,
    VxCardComponent,
    VxCardHeaderComponent,
    VxCellDirective,
    VxDataTableComponent,
    VxHeaderDirective,
    VxRowActionsDirective,
    VxEmptyStateComponent,
    VxIconComponent,
    VxIconButtonComponent,
    VxItemCellComponent,
    VxListRowComponent,
    VxMenuComponent,
    VxMenuItemComponent,
    VxMenuTriggerDirective,
    VxPanelComponent,
    VxSkeletonComponent,
    IncidentPriorityLabelComponent,
    IncidentStatusLabelComponent,
    MonitorSliValueComponent,
    MonitorStatusLabelComponent,
    IncidentsStateModule,
    MonitorsStateModule,
    ServicesStateModule,
  ],
})
export class HomePageComponent {
  private readonly store = inject(Store);
  private readonly vxDialog = inject(VxDialogService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslationService);

  // --- store data -----------------------------------------------------------

  private readonly user = this.store.selectSignal(UserState.getUser);

  private readonly incidentsSlice = this.store.selectSignal(
    IncidentsState.activeIncidents
  );

  private readonly monitorsSlice = this.store.selectSignal(
    MonitorState.monitors
  );

  private readonly servicesSlice = this.store.selectSignal(
    ServicesState.services
  );

  private readonly incidentsLoadedOnce = loadedOnce(this.incidentsSlice);

  private readonly monitorsLoadedOnce = loadedOnce(this.monitorsSlice);

  private readonly servicesLoadedOnce = loadedOnce(this.servicesSlice);

  // --- header ---------------------------------------------------------------

  /**
   * "Evening, Aaron!" — the page title. The day part is read when the title
   * recomputes rather than tracked live: nobody keeps the dashboard open
   * across the 18:00 boundary and expects the greeting to turn over.
   */
  protected readonly greeting = computed(() => {
    this.translateService.currentLocale$();
    const name = this.user()?.firstname?.trim();
    if (!name) {
      return this.translateService.translateInstantKey('menu.dashboard');
    }

    const hour = new Date().getHours();
    const dayPart =
      hour < AFTERNOON_FROM
        ? 'morning'
        : hour < EVENING_FROM
          ? 'afternoon'
          : 'evening';
    return this.translateService.translateInstantKey(
      `pages.dashboard.greeting.${dayPart}`,
      { name }
    );
  });

  // --- active incidents -----------------------------------------------------

  protected readonly incidents = computed<IncidentRow[]>(() => {
    const slice = this.incidentsSlice();
    if (!slice.entities) {
      return [];
    }
    return SortEntities(slice.entities, 'id', 'DESC')
      .map(entity => {
        const incident = entity.data!;
        return {
          id: incident.id,
          title: incident.title,
          summary: incident.summary || undefined,
          state: incident.state,
          states: incident.schemaVersion?.states ?? [],
          priority: incident.priority,
          createdAt: incident.createdAt,
        } satisfies IncidentRow;
      })
      .slice(0, ACTIVE_INCIDENT_ROWS);
  });

  protected readonly incidentsSkeleton = computed(
    () => !this.incidentsLoadedOnce() && isBusy(this.incidentsSlice())
  );

  protected readonly incidentsEmpty = computed(
    () => this.incidents().length === 0 && !this.incidentsSkeleton()
  );

  protected readonly incidentLink = (row: IncidentRow): unknown[] => [
    '/incidents',
    row.id,
  ];

  // --- monitors -------------------------------------------------------------

  protected readonly monitors = computed<MonitorRow[]>(() => {
    const slice = this.monitorsSlice();
    if (!slice.entities) {
      return [];
    }
    // Only the fetched page is ranked — the API has no status sort, so this
    // orders what the card shows rather than picking the worst overall.
    return Object.values(slice.entities)
      .map(entity => entity.data!)
      .map(monitor => ({
        id: monitor.id,
        name: monitor.name,
        target: monitorTarget(monitor),
        icon: probeTypeIcon(monitor.probeType),
        status: this.displayStatus(monitor),
        paused: monitor.paused,
        monitor,
      }))
      .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]);
  });

  protected readonly monitorsLoading = computed(
    () => !this.monitorsLoadedOnce() && isBusy(this.monitorsSlice())
  );

  /** Uptime column badge — a signal so it follows a language switch. */
  private readonly uptimeBadge = toSignal(
    this.translateService.translateKey(
      'pages.monitorOverview.kpis.windowBadge.7d'
    ),
    { initialValue: '' }
  );

  protected readonly monitorColumns = computed<VxColumn[]>(() => [
    {
      key: 'monitor',
      width: 'minmax(0,2fr)',
    },
    {
      key: 'status',
      width: 'minmax(0,1fr)',
    },
    {
      key: 'uptime',
      badge: this.uptimeBadge(),
      width: 'minmax(0,1fr)',
    },
  ]);

  protected readonly monitorLink = (row: MonitorRow): unknown[] => [
    '/monitors',
    row.id,
  ];

  // --- services -------------------------------------------------------------

  protected readonly services = computed<ServiceChip[]>(() => {
    const slice = this.servicesSlice();
    if (!slice.entities) {
      return [];
    }
    return Object.values(slice.entities)
      .map(entity => entity.data!)
      .map(service => ({
        id: service.id,
        name: service.name,
        color: service.health?.color,
      }));
  });

  protected readonly servicesSkeleton = computed(
    () => !this.servicesLoadedOnce() && isBusy(this.servicesSlice())
  );

  protected readonly servicesEmpty = computed(
    () => this.services().length === 0 && !this.servicesSkeleton()
  );

  /** Placeholder counts, so the skeletons occupy roughly the loaded height. */
  protected readonly incidentPlaceholders = [1, 2, 3];
  protected readonly servicePlaceholders = [1, 2, 3];

  constructor() {
    this.store.dispatch(
      new LoadActiveIncidents({ page: 1, pageSize: ACTIVE_INCIDENT_ROWS })
    );
    this.store.dispatch(
      new LoadMonitorsAction({
        pagination: { page: 1, pageSize: MONITOR_ROWS },
        statisticsOptions: {
          windowStart: new Date(
            Date.now() - UPTIME_WINDOW_DAYS * 24 * 60 * 60 * 1000
          ),
          resolutionSeconds: UPTIME_RESOLUTION_SECONDS,
        },
      })
    );
    this.store.dispatch(new LoadServices());
  }

  // --- template API ---------------------------------------------------------

  /** The card menu's shortcut: same dialog, the org's default schema. */
  newIncident(): void {
    const schema = creatableIncidentSchemas(
      this.store.selectSnapshot(IncidentsState.schemas).entities
    ).at(0);
    if (schema) {
      openCreateIncidentDialog(this.vxDialog, { schema });
    }
  }

  goTo(commands: unknown[]): void {
    this.router.navigate(commands);
  }

  setMonitorPaused(row: MonitorRow): void {
    this.store.dispatch(
      new UpdateMonitorAction(row.monitor, { paused: !row.paused })
    );
  }

  // --- internals ------------------------------------------------------------

  /** Same derivation the monitors list uses, so both surfaces agree. */
  private displayStatus(monitor: Monitor): MonitorDisplayStatus {
    if (monitor.paused) {
      return 'PAUSED';
    }
    if (!monitorHasAvailabilityData(monitor)) {
      return 'NO_DATA';
    }
    return refineDownForRegions(
      monitorLiveStatus(monitor),
      (monitor.regionHealth ?? []).length
    );
  }
}
